// ----------------------------------------------------------------------

function path(root: string, sublink: string) {
  return `${root}${sublink}`;
}

const ROOTS_AUTH = '/auth';
const ROOTS_DASHBOARD = '';

// ----------------------------------------------------------------------

export const PATH_AUTH = {
  root: ROOTS_AUTH,
  login: path(ROOTS_AUTH, '/login'),
  register: path(ROOTS_AUTH, '/register'),
  loginUnprotected: path(ROOTS_AUTH, '/login-unprotected'),
  registerUnprotected: path(ROOTS_AUTH, '/register-unprotected'),
  verify: path(ROOTS_AUTH, '/verify'),
  resetPassword: path(ROOTS_AUTH, '/reset-password'),
  newPassword: path(ROOTS_AUTH, '/new-password'),
};

export const PATH_PAGE = {
  comingSoon: '/coming-soon',
  maintenance: '/maintenance',
  pricing: '/pricing',
  payment: '/payment',
  about: '/about-us',
  contact: '/contact-us',
  faqs: '/faqs',
  page403: '/403',
  page404: '/404',
  page500: '/500',
  components: '/components',
};

type LandingCreateParams = {
  selectedType: string;
  programmeCode?: string;
  programmeType?: string;
}

type LandingEditParams = {
  id: string;
  programmeCode?: string;
  programmeType?: string;
}

const withQuery = (url: string, params: Record<string, string>) =>
  `${url}?${new URLSearchParams(params).toString()}`;

export const PATH_DASHBOARD = {
  root: '/dashboard',
  general: {
    app: path(ROOTS_DASHBOARD, '/dashboard'),
    businessCustomers: path(ROOTS_DASHBOARD, '/business_customers'),
    businessCustomerEdit: (id: string) => path('', `/business_customers/${id}/edit`),
    businessCustomerCreate: path('', `/business_customers/create`),
  },
  projects: {
    root: path('', '/offsets/projects'),
    project: (projectCode: string) => path('', `/offsets/projects/${projectCode}`),
  },
  baskets: {
    root: path('', '/offsets/portfolios'),
    basket: (basketId: string) => path('', `/offsets/portfolios/${basketId}`),
  },
  // end
  permissionDenied: path(ROOTS_DASHBOARD, '/permission-denied'),
  user: {
    root: path(ROOTS_DASHBOARD, '/user'),
    account: path(ROOTS_DASHBOARD, '/account'),
    plans: path(ROOTS_DASHBOARD, '/account/packages'),
    confirmation: path(ROOTS_DASHBOARD, '/account/packages/confirmation'),
    cancelConfirmation: path(ROOTS_DASHBOARD, '/account/packages/confirmation/cancel'),
  },
  credits: {
    purchase: path('', '/credits/purchase'),
    purchaseHistory: path('',  `/credits/purchase-history`),
    purchaseCredits: path('', '/credits/purchase-credits'),
  },
  cart: {
    root: path('', '/cart'),
    checkout: path('', '/checkout'),
    confirmation: (id: string) => path('', `/checkout/complete/${id}`),
    requestQuote: path('', '/cart/request-quote'),
  },
  offsets: {
    transactions: path('', '/offsets/transactions'),
    transaction: (id: string) => path('', `/offsets/transactions/${id}`),
    footprints: path('', '/offsets/footprints'),
    event: {
      amount: path('', '/offsets/footprints/event-amount'),
      calculate: path('', '/offsets/footprints/event-calculator'),
      edit: (footprintId: string) => path('', `/offsets/footprints/event-calculator/${footprintId}/edit`),
      view: (footprintId: string) => path('', `/offsets/footprints/event-calculator/${footprintId}`),
      amountEdit: (footprintId: string) => path('', `/offsets/footprints/event-amount/${footprintId}/edit`),
      amountView: (footprintId: string) => path('', `/offsets/footprints/event-amount/${footprintId}`),
    },
    business: {
      amount: path('', '/offsets/footprints/business-amount'),
      calculate: path('', '/offsets/footprints/business-calculator'),
      edit: (footprintId: string) => path('', `/offsets/footprints/business-calculator/${footprintId}/edit`),
      view: (footprintId: string) => path('', `/offsets/footprints/business-calculator/${footprintId}`),
      amountEdit: (footprintId: string) => path('', `/offsets/footprints/business-amount/${footprintId}/edit`),
      amountView: (footprintId: string) => path('', `/offsets/footprints/business-amount/${footprintId}`),
    },
    impact: path('', '/offsets/impact'),
    merchantReport: (dateFrom: string, dateTo: string) => path('', `/offsets/impact/${dateFrom}/${dateTo}/report`),
  },
  communication: {
    landingPages: path('', '/communication/pages'),
    landingCreate: ({ selectedType, programmeCode, programmeType }: LandingCreateParams) =>
      programmeCode && programmeType
        ? path('', `/programmes/${programmeType}/${programmeCode}/pages/create?type=${selectedType}`)
        : path('', `/communication/pages/create?type=${selectedType}`),
    landingEdit: ({ id, programmeCode, programmeType }: LandingEditParams) =>
      programmeCode && programmeType
        ? path('', `/programmes/${programmeType}/${programmeCode}/pages/update?id=${id}`)
        : path('', `/communication/pages/update?id=${id}`),
  },
  toolkit: {
    root: path('', '/communication/toolkit'),
    viewToolkitItem: (code: string) => path('', `/communication/toolkit/${code}`),
    impactWidget: path('', '/communication/toolkit/impact-widget'),
    flightCalculatorSettingsPage: path('', '/communication/toolkit/flight-calculator'),
  },
  individuals: path('', '/offsets/individuals'),
  businessCustomers: path(ROOTS_DASHBOARD, '/programmes/business-customers'),
  businessCustomerEdit: (id: string) => path('', `/programmes/business-customers/${id}/edit`),
  businessCustomerCreate: path('', `/programmes/business-customers/create`),
  programs: {
    root: path('', '/programmes'),
    //
    participants: (type: string, code: string) => path('', `/programmes/${type}/${code}/participants`),
    overview: (type: string, code: string) => path('', `/programmes/${type}/${code}/overview`),
    insights: (type: string, code: string) => path('', `/programmes/${type}/${code}/insights`),
    settings: (type: string, code: string) => path('', `/programmes/${type}/${code}/settings`),
    offsets: (type: string, code: string) => path('', `/programmes/${type}/${code}/offsets`),

    // TODO: @marina clean up when epic is done
    contributions: (type: string, code: string) => path('', `/programmes/${type}/${code}/contributions`),
    emissions: (type: string, code: string) => path('', `/programmes/${type}/${code}/emissions`),
    emissionRecord: (type: string, code: string, emissionRecordId: string) => path('', `/programmes/${type}/${code}/emissions/${emissionRecordId}`),
    calculate: (type: string, code: string) => path('', `/programmes/${type}/${code}/emissions/calculate`),
    addEmission: (type: string, code: string, emissionRecordId: string) => path('', `/programmes/${type}/${code}/emissions/calculate/${emissionRecordId}`),
    program: (type: string, code: string) => path('', `/programmes/${type}/${code}`),
    transactions: (type: string, code: string, queryParameters?: URLSearchParams) => path('', `/programmes/${type}/${code}/offsets${queryParameters ? `?${queryParameters.toString()}` : ''}`),
    transaction: (type: string, code: string, id: string) => path('', `/programmes/${type}/${code}/offsets/${id}`),
    employees: (type: string, code: string) => path('', `/programmes/${type}/${code}/employees`),
    customers: (type: string, code: string) => path('', `/programmes/${type}/${code}/customers`),
    flights: (type: string, code: string) => path('', `/programmes/${type}/${code}/flights`),
    impact: (type: string, code: string) => path('', `/programmes/${type}/${code}/impact`),
    eventImpact: path('', `/programmes/event/impact`),
    engagement: (type: string, code: string) => path('', `/programmes/${type}/${code}/engagement`),
    configuration: (type: string, code: string) => path('', `/programmes/${type}/${code}/configuration`),
    report: (type: string, code: string, dateFrom: string, dateTo: string) => path('', `/programmes/${type}/${code}/impact/${dateFrom}/${dateTo}/report`),
    //
    footprints: (type: string, code: string) => path('', `/programmes/${type}/${code}/footprints`),
    footprintCreate: (programmeCode: string) => path('', withQuery('/footprints/create', { programmeCode })),
    footprintEdit: (footprintId: string) => path('', `/footprints/edit/${footprintId}`),
    footprintOffsetDetails: (footprintId: string) => path('', `/footprints/offset-details/${footprintId}`),
    //
    viewReceipt: (id: string) => path('', `/offsets/${id}/receipt`),
    viewCertificate: (id: string) => path('', `/offsets/${id}/certificate`),
    viewDetailedCertificate: (id: string) => path('', `/offsets/${id}/detailed-certificate`),

    landings: (pType: string, pCode: string) => path('', `/programmes/${pType}/${pCode}/pages`),

    customer: {
      root: path('', '/programmes/customer'),
      contributions: path('', '/programmes/customer/contributions'),
    },
  },
  settings: {
    root: path('', '/settings'),
    theme: path('', '/settings/theme'),
    business: path('', '/settings/business'),
    defaultBasket: path('', '/settings/default-basket'),
    billing: path('', '/settings/billing'),
    menu: path('', '/settings/menu'),
    emails: path('', '/settings/emails'),
    dashboard: path('', '/settings/dashboard'),
  },
  account: {
    root: path('', '/account'),
    plans: path('', '/account/packages'),
    accountDetails: path('', '/account/account-details'),
    userManagement: path('', '/account/user-management')
  },
};
